import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  TruckIcon,
  WrenchScrewdriverIcon,
  ShieldCheckIcon,
  CalendarDaysIcon,
} from "@heroicons/react/24/outline";
import { Avatar, Badge, Button, EditableText, TextArea } from "../../../shared";
import { ClientsDropdown } from "../../../components";
import { DetailField } from "../../Settings/Users/DetailField";
import { useConfig } from "../../../hooks";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/access-control";
import {
  EQUIPMENT_STATUS_OPTIONS,
  FUEL_TYPE_OPTIONS,
  getEquipmentTypeOptions,
} from "../../../constants/select";
import { capitalizeFirstLetter, formatDate } from "../../../utils";
import {
  getEquipmentComplianceAlerts,
  getComplianceDateStatus,
  formatComplianceRelative,
} from "../../../utils/equipmentCompliance";
import { SectionCard } from "./components/SectionCard";
import { StatCard } from "./components/StatCard";
import { ComplianceDateField } from "./components/ComplianceDateField";
import { getAvailabilityBadge } from "../../../utils/equipmentAvailability";

const TYPE_OPTIONS_LEGACY = [
  { value: "LPS/Gas", label: "LPS/Gas" },
  { value: "Electric", label: "Electric" },
];

/**
 * Equipment overview tab — summary metrics, editable identity fields, compliance.
 */
const EquipmentOverview = ({ equipment, maintenanceHistory = [], onUpdate }) => {
  const navigate = useNavigate();
  const { appType } = useConfig();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(PERMISSIONS.EQUIPMENT_EDIT);
  const [isEditingClient, setIsEditingClient] = useState(false);
  const [notesDraft, setNotesDraft] = useState(equipment?.notes || "");

  useEffect(() => {
    setNotesDraft(equipment?.notes || "");
  }, [equipment?.notes]);

  const isTruckingEquipment =
    equipment?.equipmentCategory === "truck" ||
    equipment?.equipmentCategory === "trailer";
  const showTruckingFields = appType === "trucking" && isTruckingEquipment;

  const typeOptions = useMemo(() => {
    if (equipment?.equipmentCategory) {
      return getEquipmentTypeOptions(equipment.equipmentCategory);
    }
    return TYPE_OPTIONS_LEGACY;
  }, [equipment?.equipmentCategory]);

  const complianceAlerts = useMemo(
    () => getEquipmentComplianceAlerts(equipment),
    [equipment]
  );

  const maintenanceCount = maintenanceHistory?.length || 0;
  const recentMaintenance = (maintenanceHistory || []).slice(0, 3);

  const handleSave = async (data) => {
    if (onUpdate) {
      await onUpdate(data);
    }
  };

  const handleClientChange = async (selectedClient) => {
    await handleSave({ client: selectedClient?._id || null });
    setIsEditingClient(false);
  };

  const goToMaintenanceTab = () => {
    const params = new URLSearchParams(window.location.search);
    params.set("equipment-show", "Maintenance");
    navigate(`${window.location.pathname}?${params.toString()}`);
  };

  const complianceTone =
    complianceAlerts.length > 0
      ? complianceAlerts.some((a) => a.color === "red")
        ? "danger"
        : "warning"
      : "success";

  const nextMaintenanceStatus = getComplianceDateStatus(
    equipment?.nextMaintenanceDate
  );
  const maintenanceTone =
    nextMaintenanceStatus === "expired"
      ? "danger"
      : nextMaintenanceStatus === "warning"
        ? "warning"
        : "default";

  const EquipmentInfoEditable = ({ id, value, type, displayText, options }) => {
    if (!canEdit) {
      return (
        <span className="text-gray-900">
          {displayText ? displayText(value) : value || "—"}
        </span>
      );
    }
    return (
      <EditableText
        displayText={(e) => (
          <span className="text-gray-900">
            {displayText ? displayText(e) : e || "—"}
          </span>
        )}
        id={id}
        iconClasses="h-3 w-3"
        value={value}
        onSave={(e) => handleSave(e.data)}
        type={type}
        options={options}
      />
    );
  };

  const renderClientSection = () => {
    if (isEditingClient) {
      return (
        <ClientsDropdown
          form={{ client: equipment.client || null }}
          updateForm={(data) => {
            if (data.value) {
              handleClientChange(data.value);
            }
          }}
          value={equipment.client}
          onClick={handleClientChange}
        />
      );
    }

    if (equipment?.client) {
      return (
        <div className="flex items-center gap-3">
          <Avatar src={equipment.client.avatar} size="large" />
          <div>
            <p className="font-semibold text-gray-900">{equipment.client.name}</p>
            <p className="text-sm text-gray-500">Associated client</p>
          </div>
        </div>
      );
    }

    return (
      <p className="text-sm text-gray-500">
        No client is linked to this equipment.
      </p>
    );
  };

  const subtitleParts = [
    equipment?.licensePlate,
    equipment?.model,
    equipment?.manufacturer,
  ].filter(Boolean);

  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-6">
      {/* Hero */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm md:p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              {equipment?.equipmentCategory ? (
                <Badge
                  label={capitalizeFirstLetter(equipment.equipmentCategory)}
                  color="blue"
                />
              ) : null}
              {equipment?.status ? (
                <Badge label={equipment.status} color="gray" />
              ) : null}
              {equipment?.availability ? (
                <Badge
                  label={getAvailabilityBadge(equipment.availability).label}
                  color={getAvailabilityBadge(equipment.availability).color}
                />
              ) : null}
            </div>
            <div className="mt-2">
              {canEdit ? (
                <EditableText
                  className="text-2xl font-bold text-gray-900"
                  iconClasses="h-4 w-4"
                  id="name"
                  value={equipment?.name}
                  onSave={(e) => handleSave(e.data)}
                />
              ) : (
                <h1 className="text-2xl font-bold text-gray-900">
                  {equipment?.name || "Unnamed equipment"}
                </h1>
              )}
            </div>
            {subtitleParts.length > 0 ? (
              <p className="mt-1 text-sm text-gray-600">
                {subtitleParts.join(" · ")}
              </p>
            ) : null}
            {equipment?.serialNumber ? (
              <p className="mt-1 text-xs text-gray-500">
                S/N {equipment.serialNumber}
              </p>
            ) : null}
          </div>
          {canEdit ? (
            <div className="w-full md:w-56">
              <p className="mb-1 text-xs font-medium uppercase text-gray-500">
                Status
              </p>
              <EquipmentInfoEditable
                id="status"
                value={equipment?.status}
                type="select"
                options={EQUIPMENT_STATUS_OPTIONS}
              />
            </div>
          ) : null}
        </div>

        {complianceAlerts.length > 0 ? (
          <div
            className="mt-4 flex flex-wrap gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2"
            role="status"
          >
            <ShieldCheckIcon className="h-5 w-5 shrink-0 text-amber-700" />
            <div className="flex flex-wrap gap-1">
              {complianceAlerts.map((alert) => (
                <Badge
                  key={alert.key}
                  label={alert.label}
                  color={alert.color}
                />
              ))}
            </div>
          </div>
        ) : showTruckingFields ? (
          <div className="mt-4 flex items-center gap-2 text-sm text-green-700">
            <ShieldCheckIcon className="h-5 w-5" />
            Compliance checks are up to date
          </div>
        ) : null}
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Compliance"
          value={complianceAlerts.length ? `${complianceAlerts.length} alert(s)` : "All clear"}
          subtext={
            showTruckingFields
              ? "Registration, insurance, inspection"
              : "Trucking fleet only"
          }
          icon={<ShieldCheckIcon className="h-5 w-5" />}
          tone={complianceTone}
        />
        <StatCard
          label="Next maintenance"
          value={
            equipment?.nextMaintenanceDate
              ? formatDate(equipment.nextMaintenanceDate)
              : "Not scheduled"
          }
          subtext={formatComplianceRelative(equipment?.nextMaintenanceDate)}
          icon={<WrenchScrewdriverIcon className="h-5 w-5" />}
          tone={maintenanceTone}
        />
        <StatCard
          label="Maintenance records"
          value={String(maintenanceCount)}
          subtext={
            maintenanceCount
              ? "View full history on Maintenance tab"
              : "No records yet"
          }
          icon={<CalendarDaysIcon className="h-5 w-5" />}
        />
        {showTruckingFields ? (
          <StatCard
            label="Unit type"
            value={capitalizeFirstLetter(equipment?.equipmentCategory || "—")}
            subtext={equipment?.type || "Type not set"}
            icon={<TruckIcon className="h-5 w-5" />}
          />
        ) : (
          <StatCard
            label="Equipment type"
            value={equipment?.type || "—"}
            subtext={equipment?.model || "Model not set"}
            icon={<WrenchScrewdriverIcon className="h-5 w-5" />}
          />
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <SectionCard title="Identification">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <DetailField label="Name">
                <EquipmentInfoEditable id="name" value={equipment?.name} />
              </DetailField>
              <DetailField label="Serial number">
                <EquipmentInfoEditable
                  id="serialNumber"
                  value={equipment?.serialNumber}
                />
              </DetailField>
              <DetailField label="Model">
                <EquipmentInfoEditable id="model" value={equipment?.model} />
              </DetailField>
              <DetailField label="Manufacturer">
                <EquipmentInfoEditable
                  id="manufacturer"
                  value={equipment?.manufacturer}
                />
              </DetailField>
              {equipment?.equipmentCategory ? (
                <DetailField label="Category">
                  {capitalizeFirstLetter(equipment.equipmentCategory)}
                </DetailField>
              ) : null}
              <DetailField label="Type">
                <EquipmentInfoEditable
                  id="type"
                  value={equipment?.type}
                  type="select"
                  options={typeOptions}
                />
              </DetailField>
            </div>
          </SectionCard>

          {showTruckingFields ? (
            <SectionCard
              title="Vehicle details"
              description="DOT, registration, and unit identifiers"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <DetailField label="VIN">
                  <EquipmentInfoEditable id="vin" value={equipment?.vin} />
                </DetailField>
                <DetailField label="License plate">
                  <EquipmentInfoEditable
                    id="licensePlate"
                    value={equipment?.licensePlate}
                  />
                </DetailField>
                <DetailField label="Year">
                  <EquipmentInfoEditable
                    id="year"
                    value={equipment?.year}
                    type="number"
                  />
                </DetailField>
                <DetailField label="DOT number">
                  <EquipmentInfoEditable
                    id="dotNumber"
                    value={equipment?.dotNumber}
                  />
                </DetailField>
                <DetailField label="Fuel type">
                  {canEdit ? (
                    <EquipmentInfoEditable
                      id="fuelType"
                      value={equipment?.fuelType}
                      type="select"
                      options={FUEL_TYPE_OPTIONS}
                      displayText={(v) =>
                        v ? capitalizeFirstLetter(v) : "—"
                      }
                    />
                  ) : (
                    capitalizeFirstLetter(equipment?.fuelType) || "—"
                  )}
                </DetailField>
              </div>
            </SectionCard>
          ) : null}

          {showTruckingFields ? (
            <SectionCard
              title="Compliance & registration"
              description="Expiry dates with automatic status indicators"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <ComplianceDateField
                  label="Registration expiry"
                  date={equipment?.registrationExpiry}
                />
                <ComplianceDateField
                  label="Insurance expiry"
                  date={equipment?.insuranceExpiry}
                />
                <ComplianceDateField
                  label="Last inspection"
                  date={equipment?.lastInspectionDate}
                />
                <ComplianceDateField
                  label="Next inspection due"
                  date={equipment?.nextInspectionDue}
                />
              </div>
            </SectionCard>
          ) : null}

          <SectionCard title="Maintenance schedule">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <DetailField label="Purchase date">
                {equipment?.purchaseDate
                  ? formatDate(equipment.purchaseDate)
                  : "—"}
              </DetailField>
              <DetailField label="Last maintenance">
                {equipment?.lastMaintenanceDate
                  ? formatDate(equipment.lastMaintenanceDate)
                  : "—"}
              </DetailField>
              <DetailField label="Next maintenance">
                {equipment?.nextMaintenanceDate
                  ? formatDate(equipment.nextMaintenanceDate)
                  : "—"}
              </DetailField>
              {appType === "serviceRepair" ? (
                <DetailField label="Hour meter">
                  <EquipmentInfoEditable
                    id="odometer"
                    value={equipment?.odometer}
                    type="number"
                  />
                </DetailField>
              ) : null}
            </div>
          </SectionCard>

          {canEdit ? (
            <SectionCard
              title="Notes"
              actions={
                notesDraft !== (equipment?.notes || "") ? (
                  <Button
                    size="sm"
                    onClick={() => handleSave({ notes: notesDraft })}
                  >
                    Save notes
                  </Button>
                ) : null
              }
            >
              <TextArea
                id="notes"
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                placeholder="Internal notes about this unit…"
                rows={4}
              />
            </SectionCard>
          ) : equipment?.notes ? (
            <SectionCard title="Notes">
              <p className="whitespace-pre-wrap text-sm text-gray-700">
                {equipment.notes}
              </p>
            </SectionCard>
          ) : null}
        </div>

        <div className="space-y-4">
          {appType === "serviceRepair" ? (
            <SectionCard
              title="Associated client"
              actions={
                canEdit ? (
                  <Button
                    variant="text"
                    size="sm"
                    onClick={() => setIsEditingClient(!isEditingClient)}
                  >
                    {isEditingClient ? "Cancel" : "Change"}
                  </Button>
                ) : null
              }
            >
              {renderClientSection()}
            </SectionCard>
          ) : null}

          <SectionCard
            title="Maintenance history"
            actions={
              <Button variant="text" size="sm" onClick={goToMaintenanceTab}>
                View all
              </Button>
            }
          >
            {recentMaintenance.length === 0 ? (
              <p className="text-sm text-gray-500">
                No maintenance records yet. Open the Maintenance tab to add
                service history.
              </p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {recentMaintenance.map((record, index) => (
                  <li
                    key={record?._id || index}
                    className="flex justify-between gap-2 py-2 text-sm"
                  >
                    <span className="font-medium text-gray-800">
                      {record?.description ||
                        record?.type ||
                        record?.name ||
                        "Service record"}
                    </span>
                    <span className="shrink-0 text-gray-500">
                      {record?.date
                        ? formatDate(record.date)
                        : record?.serviceDate
                          ? formatDate(record.serviceDate)
                          : "—"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {canEdit ? (
              <Button
                className="mt-3 w-full"
                size="sm"
                type="alternative"
                onClick={goToMaintenanceTab}
              >
                Manage maintenance
              </Button>
            ) : null}
          </SectionCard>
        </div>
      </div>
    </div>
  );
};

export default EquipmentOverview;
